import { useEffect, useRef, useState } from 'react';
import { CgMenuLeft } from 'react-icons/cg';
import { FaChevronDown } from 'react-icons/fa';
// import { useNavigate } from 'react-router-dom';
import { logoutUser } from '../redux/actions/authActions';
import { fetchProfile } from '../redux/actions/profileActions';
import { clearProfile } from '../redux/slices/profileSlice';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { Link } from 'react-router-dom';
import User from '../assets/user.png';

interface NavigationProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
  isMobile?: boolean;
}

export default function Navigation({ isSidebarOpen, setIsSidebarOpen, isMobile }: NavigationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useAppDispatch();
  // Get user data from Redux store
  const { user } = useAppSelector(state => state.auth);
  const { data: profileData } = useAppSelector(state => state.profile);
  // const navigate = useNavigate();

  // Get designation display safely
  const getDesignationDisplay = () => {
    // First try to get from profile data (most complete)
    if (profileData?.designation) {
      if (typeof profileData.designation === 'object' && profileData.designation !== null) {
        if ('name' in profileData.designation) {
          return String(profileData.designation.name).toUpperCase();
        }
      }
      return String(profileData.designation).toUpperCase();
    }

    // Fall back to auth user designation
    if (user?.designation) {
      if (typeof user.designation === 'object' && user.designation !== null) {
        if (user.designation && 'name' in user.designation) {
          return String((user.designation as any).name).toUpperCase();
        }
      }
      return String(user.designation).toUpperCase();
    }

    return 'User';
  };

  // Get user's full name safely
  const getUserName = () => {
    // First try to get from profile data
    if (profileData?.fullname) {
      return profileData.fullname;
    }

    // Fall back to auth user data
    if (user?.fullname) {
      return user.fullname;
    }

    // Last resort - use email
    if (user?.email) {
      return user.email.split('@')[0];
    }

    return 'User';
  };

  // Get profile picture URL
  const getProfilePictureUrl = () => {
    return profileData?.profilePicture || User;
  };

  // Fetch profile data when component mounts or user changes
  useEffect(() => {
    if (user?.id) {
      console.log('Navigation: User detected, fetching profile data for user ID:', user.id);
      dispatch(fetchProfile())
        .unwrap()
        .then(response => {
          console.log('Navigation: Profile data fetched successfully:', response.data);
        })
        .catch(error => {
          console.error('Navigation: Failed to fetch profile data:', error);
        });
    }
  }, [dispatch, user?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only run this effect if the dropdown is open
      if (!isOpen) return;

      // Check if click is outside dropdown and outside button
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle logout with improved error handling and state management
  const handleLogout = async () => {
    try {
      console.log('Logout initiated');

      // Set loading state to prevent multiple clicks
      setIsOpen(false);

      // Determine role from user data in Redux store
      const role = user?.role?.toLowerCase() || 'employee';
      console.log(`Logging out with role: ${role}`);

      // Clear cookies first to prevent auth issues during logout
      document.cookie = 'pms_admin_=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'pms_employee_=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

      // Clear localStorage immediately
      localStorage.removeItem('auth');
      localStorage.removeItem('users-data');
      localStorage.removeItem('profile'); // Clear profile from localStorage

      // Clear profile from Redux store
      dispatch(clearProfile());

      // Try to call logout API, but don't wait for it if it fails
      try {
        await dispatch(logoutUser({ role })).unwrap();
        console.log('Logout API call successful');
      } catch (apiError) {
        console.warn('Logout API call failed, but continuing with client-side logout:', apiError);
      }

      // Force redirect with a small delay to ensure cleanup completes
      setTimeout(() => {
        window.location.href = '/auth/login';
      }, 100);
    } catch (error: any) {
      console.error('Logout failed:', error);

      // Even if logout fails, clear local data and redirect
      document.cookie = 'pms_admin_=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'pms_employee_=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      localStorage.removeItem('auth');
      localStorage.removeItem('users-data');
      localStorage.removeItem('profile');

      // Clear profile from Redux store
      dispatch(clearProfile());

      // Force redirect anyway
      window.location.href = '/auth/login';
    }
  };

  return (
    <>
      <nav className="flex items-center justify-between py-4 relative z-10">
        {/* Left: Hamburger Menu */}
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 rounded-md hover:bg-gray-200 transition-all duration-300"
          aria-label={
            isMobile
              ? 'Toggle sidebar'
              : isSidebarOpen
                ? 'Switch to mini sidebar'
                : 'Show full sidebar'
          }
        >
          <CgMenuLeft size={30} />
        </button>

        {/* Right: Icons Group - Notification Removed */}
        <div className="flex items-center gap-4">

          {/* User Profile */}
          <div className="relative">
            <button
              ref={buttonRef}
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-2 px-3 py-1 bg-white rounded-full hover:bg-gray-100"
            >
              <img
                src={getProfilePictureUrl()}
                alt="User Avatar"
                className="w-8 h-8 rounded-full object-cover"
                onError={e => {
                  console.error(
                    'Navigation profile picture failed to load:',
                    getProfilePictureUrl()
                  );
                  e.currentTarget.src = User;
                }}
              />
              <div className="text-left hidden md:block">
                <p className="text-sm font-semibold">{getUserName()}</p>
                <p className="text-xs text-blue-400 font-semibold">{getDesignationDisplay()}</p>
              </div>
              <FaChevronDown size={14} className="hidden md:block" />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
              <div
                ref={dropdownRef}
                className="absolute right-0 w-48 mt-2 bg-white border rounded-md shadow-lg z-40"
              >
                <div className="md:hidden px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-semibold">{getUserName()}</p>
                  <p className="text-xs text-blue-400 font-semibold">{getDesignationDisplay()}</p>
                </div>
                <Link to="/profile" className="block px-4 py-2 text-sm hover:bg-gray-100">
                  Profile
                </Link>

                <button
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleLogout();
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-gray-100"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
    </>
  );
}
